import type { Entry, Asset } from 'contentful';
import { isArray } from './guards/isArray';
import { isDefined } from './guards/isDefined';
import { isEntryOrAsset } from './guards/isEntryOrAsset';
import type { EntryWithLinkData } from '~~/lib/ContentfulService';
import type { EntryLink } from '~/utils/guards/isEntryLink';
import { isEntryLink } from '~/utils/guards/isEntryLink';
import { Tracer } from '~~/lib/Tracing';
import {
  isEntryResolveError,
  type ContentfulEntryNotResolvableError
} from '~/utils/guards/isEntryResolveError';
import {
  isEntryNotAvailableInLanguageError,
  type ContentfulEntryNotAvailableInLanguageError
} from '~/utils/guards/isEntryNotAvailableInLanguageError';
import { cloneDeep } from '~/utils/cloneDeep';
import {
  logContentfulReferenceRemoved,
  type ContentfulReferenceRemovedLocation
} from '~/utils/logContentfulReferenceRemoved';

export type ResolvedEntryTypes =
  // TODO: encapsulate the `Entry` type and always remove methods.
  | Omit<Entry<unknown>, 'toPlainObject' | 'update'>
  | EntryLink
  | Asset
  | ContentfulEntryNotResolvableError
  | ContentfulEntryNotAvailableInLanguageError;

export type ResolveEntryDiagnostics = {
  /** When true (Contentful preview in the browser), log stripped references to the console. */
  warnRemovedReferencesInBrowser?: boolean;
  /**
   * Defaults to true to preserve historical behavior. Server paths that receive
   * a fresh EntryWithLinkData object can opt out to avoid cloning very large
   * Contentful graphs before resolving them.
   */
  cloneInput?: boolean;
};

/**
 * Resolves the given entry recursive with the given linked entries.
 *
 * @param data The data to resolve.
 * @param removeUnresolvedLinksAndErrors
 * @param diagnostics Optional client-side warnings when references are stripped in preview.
 */
export function resolveEntry<T extends ResolvedEntryTypes>(
  data: EntryWithLinkData<T>,
  removeUnresolvedLinksAndErrors = false,
  diagnostics?: ResolveEntryDiagnostics
): T {
  const tracer = new Tracer('utils');

  return tracer.withSyncSpan('resolveEntry', {}, () => {
    data = diagnostics?.cloneInput === false ? data : cloneDeep(data);
    return walk(
      data.entry as Walkable,
      data,
      removeUnresolvedLinksAndErrors,
      {},
      {},
      diagnostics
    ) as T;
  });
}

function appendFieldPathSegment(
  location: ContentfulReferenceRemovedLocation,
  key: string
): ContentfulReferenceRemovedLocation {
  return {
    containingEntryId: location.containingEntryId,
    fieldPath: location.fieldPath ? `${location.fieldPath}.${key}` : key
  };
}

function appendArrayIndexToFieldPath(
  location: ContentfulReferenceRemovedLocation,
  index: number
): ContentfulReferenceRemovedLocation {
  return {
    containingEntryId: location.containingEntryId,
    fieldPath: location.fieldPath
      ? `${location.fieldPath}[${index}]`
      : `[${index}]`
  };
}

function walk<T extends Walkable>(
  value: Walkable,
  data: EntryWithLinkData<ResolvedEntryTypes>,
  removeUnresolvedLinksAndErrors = false,
  cache: Record<string, Entry<unknown> | Asset | undefined> = {},
  location: ContentfulReferenceRemovedLocation = {},
  diagnostics?: ResolveEntryDiagnostics
): T {
  if (isEntryLink(value)) {
    const entry = data.linkedEntries[value.sys.id];

    // A linkedEntries value must be a target, not another link placeholder.
    // Treat cached placeholders as unresolved instead of recursing forever.
    if (entry && !isEntryLink(entry)) {
      return walk(
        entry as Walkable,
        data,
        removeUnresolvedLinksAndErrors,
        cache,
        {},
        diagnostics
      );
    }

    if (removeUnresolvedLinksAndErrors) {
      logContentfulReferenceRemoved(value, location, {
        warnInBrowser: diagnostics?.warnRemovedReferencesInBrowser
      });
      return undefined as unknown as T;
    }

    const errorEntry = data.errors[value.sys.id];
    if (errorEntry) {
      return errorEntry as unknown as T;
    }

    return value as T;
  }

  if (
    ((isEntryResolveError(value) as boolean) ||
      (isEntryNotAvailableInLanguageError(value) as boolean)) &&
    removeUnresolvedLinksAndErrors
  ) {
    logContentfulReferenceRemoved(value, location, {
      warnInBrowser: diagnostics?.warnRemovedReferencesInBrowser
    });
    return undefined as unknown as T;
  }

  if (isEntryOrAsset(value)) {
    if (!cache[value.sys.id]) {
      cache[value.sys.id] = value;

      if (typeof value.fields === 'object' && value.fields) {
        value.fields =
          walk(
            value.fields,
            data,
            removeUnresolvedLinksAndErrors,
            cache,
            { containingEntryId: value.sys.id },
            diagnostics
          ) ?? {};
      }
    }

    return cache[value.sys.id] as T;
  }

  if (isArray(value)) {
    const mapped = value.map((item, index) =>
      isWalkable(item)
        ? walk(
            item,
            data,
            removeUnresolvedLinksAndErrors,
            cache,
            appendArrayIndexToFieldPath(location, index),
            diagnostics
          )
        : item
    );
    if (removeUnresolvedLinksAndErrors) {
      return mapped.filter(isDefined) as T;
    }
    return mapped as T;
  }

  for (const key in value) {
    const val = value[key];

    if (isWalkable(val)) {
      value[key] = walk(
        val,
        data,
        removeUnresolvedLinksAndErrors,
        cache,
        appendFieldPathSegment(location, key),
        diagnostics
      );
    }
  }

  return value as T;
}

type Walkable =
  | Partial<Record<string, unknown>>
  | unknown[]
  | EntryLink
  | Entry<unknown>
  | Asset;

function isWalkable(value: unknown): value is Walkable {
  return (
    isDefined(value) &&
    (isEntryOrAsset(value) ||
      isEntryLink(value) ||
      isArray(value) ||
      typeof value === 'object')
  );
}
